import { useState, useRef, ChangeEvent } from "react";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar, MoreHorizontal, PlusCircle, Upload, Download, Trash2, FileText } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { format } from "date-fns";
import { Member, NewMember, ChitPool } from "@/lib/types";
import { processExcelUpload, downloadMemberTemplate } from "@/lib/excelUtils";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import PaymentTracker from "@/components/PaymentTracker";

interface MemberListProps {
  chitPoolId: string;
  chitPoolName?: string; // Add this new optional prop
  chitPool?: ChitPool; // Add chitPool prop for payment tracking
  members: Member[];
  onAddMember: (member: NewMember) => Promise<void>;
  onUpdateMember: (id: string, member: Partial<{
    name: string;
    whatsappNumber: string;
    chitPoolId: string;
    disbursedAmount: number | null;
  }>) => Promise<void>;
  onDeleteMember: (id: string) => Promise<void>;
  onToggleLift: (id: string, hasLifted: boolean, disbursedAmount?: number | null) => Promise<void>;
  onUpdateDisbursedAmount?: (memberId: string, amount: number) => Promise<void>;
  onPaymentUpdate?: () => void; // Add callback for payment updates
}

const MemberList = ({
  chitPoolId,
  chitPoolName = "Chit-Pool", // Default name if not provided
  chitPool,
  members,
  onAddMember,
  onUpdateMember,
  onDeleteMember,
  onToggleLift,
  onUpdateDisbursedAmount,
  onPaymentUpdate,
}: MemberListProps) => {
  const [newMemberName, setNewMemberName] = useState("");
  const [newMemberPhone, setNewMemberPhone] = useState("");
  const [editMemberId, setEditMemberId] = useState<string | null>(null);
  const [editMemberName, setEditMemberName] = useState("");
  const [editMemberPhone, setEditMemberPhone] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isLiftDialogOpen, setIsLiftDialogOpen] = useState(false);
  const [memberToLift, setMemberToLift] = useState<string | null>(null);
  const [disbursedAmount, setDisbursedAmount] = useState<string>("");
  const [isUpdateDisbursedAmountDialogOpen, setIsUpdateDisbursedAmountDialogOpen] = useState(false);
  const [memberToUpdateDisbursedAmount, setMemberToUpdateDisbursedAmount] = useState<Member | null>(null);
  const [updatedDisbursedAmount, setUpdatedDisbursedAmount] = useState<string>("");
  // Remove isDropdownOpen state as it's causing conflicts - let radix-ui handle its own state
  const [isReportsDropdownOpen, setIsReportsDropdownOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const handleAddMember = async () => {
    if (!newMemberName.trim() || !newMemberPhone.trim()) return;
    
    setIsSubmitting(true);
    try {
      const memberData: NewMember = {
        name: newMemberName,
        whatsappNumber: newMemberPhone,
        chitPoolId,
        hasLifted: false,
        createdAt: new Date().toISOString(),
      };
      
      await onAddMember(memberData);
      setNewMemberName("");
      setNewMemberPhone("");
      setIsAddDialogOpen(false);
    } catch (error) {
      console.error("Error adding member:", error);
      toast.error("Failed to add member");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditMember = async () => {
    if (!editMemberId || !editMemberName.trim() || !editMemberPhone.trim()) return;
    
    setIsSubmitting(true);
    try {
      await onUpdateMember(editMemberId, {
        name: editMemberName,
        whatsappNumber: editMemberPhone,
        chitPoolId,
      });
      setIsEditDialogOpen(false);
    } catch (error) {
      console.error("Error updating member:", error);
      toast.error("Failed to update member");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (id: string) => {
    setMemberToDelete(id);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!memberToDelete) return;
    
    setIsSubmitting(true);
    try {
      await onDeleteMember(memberToDelete);
      setIsDeleteDialogOpen(false);
      setMemberToDelete(null);
    } catch (error) {
      console.error("Error deleting member:", error);
      toast.error("Failed to delete member");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditDialog = (member: Member) => {
    setEditMemberId(member.id);
    setEditMemberName(member.name);
    setEditMemberPhone(member.whatsappNumber);
    setIsEditDialogOpen(true);
  };

  const handleLiftMember = (id: string) => {
    setMemberToLift(id);
    setDisbursedAmount("");
    setIsLiftDialogOpen(true);
  };

  const handleLiftConfirm = async () => {
    if (!memberToLift) return;
    
    setIsSubmitting(true);
    try {
      const amount = disbursedAmount ? parseFloat(disbursedAmount) : null;
      if (amount === null || isNaN(amount) || amount <= 0) {
        toast.error("Please enter a valid disbursed amount.");
        setIsSubmitting(false);
        return;
      }
      await onToggleLift(memberToLift, true, amount);
      setIsLiftDialogOpen(false);
      setMemberToLift(null);
      setDisbursedAmount("");
      members = members.map(member =>
        member.id === memberToLift
          ? { ...member, hasLifted: true, disbursedAmount: amount, liftedDate: new Date().toISOString() }
          : member
      );
    } catch (error) {
      console.error("Error lifting member:", error);
      toast.error("Failed to lift member");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUnliftMember = async (id: string) => {
    setIsSubmitting(true);
    try {
      await onToggleLift(id, false, null);
    } catch (error) {
      console.error("Error unlifting member:", error);
      members = members.map(member =>
        member.id === id
          ? { ...member, hasLifted: false, disbursedAmount: null, liftedDate: null }
          : member
      );

      toast.error("Failed to update member status");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openUpdateDisbursedAmountDialog = (member: Member) => {
    setMemberToUpdateDisbursedAmount(member);
    setUpdatedDisbursedAmount(member.disbursedAmount?.toString() || "");
    setIsUpdateDisbursedAmountDialogOpen(true);
  };

  const handleUpdateDisbursedAmountConfirm = async () => {
    if (!memberToUpdateDisbursedAmount || !updatedDisbursedAmount) return;

    const amount = parseFloat(updatedDisbursedAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid positive disbursed amount.");
      return;
    }

    setIsSubmitting(true);
    try {
      // Use onUpdateDisbursedAmount if provided, otherwise fall back to onUpdateMember
      if (onUpdateDisbursedAmount) {
        await onUpdateDisbursedAmount(memberToUpdateDisbursedAmount.id, amount);
      } else {
        await onUpdateMember(memberToUpdateDisbursedAmount.id, {
          name: memberToUpdateDisbursedAmount.name,
          whatsappNumber: memberToUpdateDisbursedAmount.whatsappNumber,
          chitPoolId,
        });
      }
      
      setIsUpdateDisbursedAmountDialogOpen(false);
      setMemberToUpdateDisbursedAmount(null);
      setUpdatedDisbursedAmount("");
      toast.success("Disbursed amount updated successfully.");
    } catch (error) {
      console.error("Error updating disbursed amount:", error);
      toast.error("Failed to update disbursed amount");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsSubmitting(true);
    try {
      const members = await processExcelUpload(file);
      
      if (members.length === 0) {
        toast.error("No valid members found in the Excel file");
        return;
      }
      
      let addedCount = 0;
      for (const member of members) {
        const memberData: NewMember = {
          name: member.name,
          whatsappNumber: member.whatsappNumber,
          chitPoolId,
          hasLifted: member.hasLifted || false,
          liftedDate: member.liftedDate || null,
          disbursedAmount: member.disbursedAmount || 0,
          createdAt: new Date().toISOString(),
        };
        
        try {
          await onAddMember(memberData);
          addedCount++;
        } catch (error) {
          console.error("Error adding member from Excel:", error);
        }
      }
      
      setIsUploadDialogOpen(false);
      toast.success(`Added ${addedCount} members from Excel file`);
      
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      console.error("Error processing Excel file:", error);
      toast.error("Failed to process the Excel file. Please check the format");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadTemplate = () => {
    downloadMemberTemplate();
  };

  const handleExportMembersExcel = () => {
    const worksheetData = members.map(member => ({
      "Name": member.name,
      "WhatsApp Number": member.whatsappNumber,
      "Lifted Status": member.hasLifted ? "Yes" : "No",
      "Lift Date": member.liftedDate ? format(new Date(member.liftedDate), "dd/MM/yyyy") : "-",
      "Disbursed Amount": member.disbursedAmount ? `₹${member.disbursedAmount}` : "-"
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "All Members");

    const sanitizedChitPoolName = (chitPoolName || "Chit-Pool").replace(/[^a-zA-Z0-9]/g, "_");
    XLSX.writeFile(workbook, `${sanitizedChitPoolName}-members-${format(new Date(), "yyyy-MM-dd")}.xlsx`);
    toast.success("Members list exported successfully");
  };

  const handleExportLiftedMembersExcel = () => {
    const liftedMembers = members.filter(member => member.hasLifted);

    const worksheetData = liftedMembers.map(member => ({
      "Name": member.name,
      "WhatsApp Number": member.whatsappNumber,
      "Lift Date": member.liftedDate ? format(new Date(member.liftedDate), "dd/MM/yyyy") : "-",
      "Disbursed Amount": member.disbursedAmount ? `₹${member.disbursedAmount}` : "-"
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Lifted Members");

    const sanitizedChitPoolName = (chitPoolName || "Chit-Pool").replace(/[^a-zA-Z0-9]/g, "_");
    try {
      XLSX.writeFile(workbook, `${sanitizedChitPoolName}-lifted-members-${format(new Date(), "yyyy-MM-dd")}.xlsx`);
      toast.success("Lifted members list exported successfully");
    } catch (error) {
      toast.error("Failed to export lifted members list. Please try again.");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:justify-between gap-3 mb-4">
        <div className="flex gap-2">
          <Button 
            size="sm" 
            onClick={() => setIsAddDialogOpen(true)}
          >
            Add Member
          </Button>
          
          {/* Hide Reports dropdown on mobile */}
          <DropdownMenu 
            open={isReportsDropdownOpen} 
            onOpenChange={setIsReportsDropdownOpen}
          >
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" size="sm" className="gap-1 hidden sm:inline-flex">
                <FileText className="h-4 w-4" />
                Reports
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onClick={handleExportMembersExcel}>
                <Download className="h-4 w-4 mr-2" />
                Export All Members
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportLiftedMembersExcel}>
                <Download className="h-4 w-4 mr-2" />
                Export Lifted Members
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        {/* Hide Upload/Download buttons on mobile */}
        <div className="hidden sm:flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setIsUploadDialogOpen(true)} 
            className="gap-1"
          >
            <Upload className="h-4 w-4" />
            Upload Excel
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleDownloadTemplate} 
            className="gap-1"
          >
            <Download className="h-4 w-4" />
            Download Template
          </Button>
        </div>
      </div>

      {members.length > 0 ? (
        <>
          {/* Desktop Table View */}
          <div className="hidden md:block border rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">Lifted</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>WhatsApp Number</TableHead>
                  <TableHead>Lift Date</TableHead>
                  <TableHead>Disbursed Amount</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell>
                      <Checkbox
                        checked={member.hasLifted}
                        onCheckedChange={(checked) => {
                          if (typeof checked === "boolean") {
                            if (checked) {
                              if (!member.hasLifted) {
                                handleLiftMember(member.id);
                              } else {
                                toast.info("Member already lifted. Use the menu to update amount.");
                              }
                            } else {
                              handleUnliftMember(member.id);
                            }
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell className="font-medium">{member.name}</TableCell>
                    <TableCell>{member.whatsappNumber}</TableCell>
                    <TableCell>
                      {member.liftedDate ? (
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                          {format(new Date(member.liftedDate), "dd MMM yyyy")}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">Not lifted yet</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {member.disbursedAmount ? (
                        <span>₹{member.disbursedAmount.toLocaleString()}</span>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {chitPool && (
                          <PaymentTracker 
                            member={member}
                            chitPool={chitPool}
                            onPaymentUpdate={() => onPaymentUpdate?.()}
                          />
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                              <span className="sr-only">Open menu</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(member)}>
                              Edit Member Details
                            </DropdownMenuItem>
                            {member.hasLifted && (
                              <DropdownMenuItem onClick={() => openUpdateDisbursedAmountDialog(member)}>
                                Update Disbursed Amount
                              </DropdownMenuItem>
                            )}
                            {!member.hasLifted && (
                              <DropdownMenuItem onClick={() => handleLiftMember(member.id)}>
                                Lift Member
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleDeleteClick(member.id)}>
                              Delete Member
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {members.map((member) => (
              <Card key={member.id} className="p-4">
                <div className="space-y-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-medium text-left">{member.name}</h4>
                      <p className="text-sm text-muted-foreground text-left">{member.whatsappNumber}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={member.hasLifted}
                        onCheckedChange={(checked) => {
                          if (typeof checked === "boolean") {
                            if (checked) {
                              if (!member.hasLifted) {
                                handleLiftMember(member.id);
                              } else {
                                toast.info("Member already lifted. Use the menu to update amount.");
                              }
                            } else {
                              handleUnliftMember(member.id);
                            }
                          }
                        }}
                      />
                      <label className="text-sm">Lifted</label>
                    </div>
                  </div>
                  
                  {member.hasLifted && (
                    <div className="space-y-2 pt-2 border-t">
                      {member.liftedDate && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Lift Date</span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {format(new Date(member.liftedDate), "dd MMM yyyy")}
                          </span>
                        </div>
                      )}
                      {member.disbursedAmount && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Disbursed</span>
                          <span className="font-medium">₹{member.disbursedAmount.toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="flex flex-col sm:flex-row gap-2 pt-2">
                    {chitPool && (
                      <PaymentTracker 
                        member={member}
                        chitPool={chitPool}
                        onPaymentUpdate={() => onPaymentUpdate?.()}
                      />
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="w-full sm:w-auto">
                          <MoreHorizontal className="h-4 w-4 mr-1" />
                          <span>Options</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(member)}>
                          Edit Member Details
                        </DropdownMenuItem>
                        {member.hasLifted && (
                          <DropdownMenuItem onClick={() => openUpdateDisbursedAmountDialog(member)}>
                            Update Disbursed Amount
                          </DropdownMenuItem>
                        )}
                        {!member.hasLifted && (
                          <DropdownMenuItem onClick={() => handleLiftMember(member.id)}>
                            Lift Member
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => handleDeleteClick(member.id)}>
                          Delete Member
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </>
      ) : (
        <div className="text-center py-8 border rounded-md">
          <p className="text-muted-foreground">No members added yet</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => setIsAddDialogOpen(true)}
          >
            <PlusCircle className="h-4 w-4 mr-2" />
            Add your first member
          </Button>
        </div>
      )}

      {/* Add Member Dialog */}
      <Dialog 
        open={isAddDialogOpen} 
        onOpenChange={(open) => {
          if (!isSubmitting) {
            setIsAddDialogOpen(open);
            if (!open) {
              setNewMemberName("");
              setNewMemberPhone("");
            }
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Member</DialogTitle>
            <DialogDescription>
              Add a new member to this chit pool.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                placeholder="Enter member name"
                value={newMemberName}
                onChange={(e) => setNewMemberName(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="phone">WhatsApp Number</Label>
              <Input
                id="phone"
                placeholder="Enter WhatsApp number"
                value={newMemberPhone}
                onChange={(e) => setNewMemberPhone(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsAddDialogOpen(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button onClick={handleAddMember} disabled={isSubmitting || !newMemberName || !newMemberPhone}>
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                  Adding...
                </span>
              ) : (
                'Add Member'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog 
        open={isEditDialogOpen} 
        onOpenChange={(open) => {
          if (!isSubmitting) {
            setIsEditDialogOpen(open);
            if (!open) {
              setEditMemberId(null);
              setEditMemberName("");
              setEditMemberPhone("");
            }
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Member</DialogTitle>
            <DialogDescription>
              Update member information.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                placeholder="Enter member name"
                value={editMemberName}
                onChange={(e) => setEditMemberName(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-phone">WhatsApp Number</Label>
              <Input
                id="edit-phone"
                placeholder="Enter WhatsApp number"
                value={editMemberPhone}
                onChange={(e) => setEditMemberPhone(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsEditDialogOpen(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button onClick={handleEditMember} disabled={isSubmitting || !editMemberName || !editMemberPhone}>
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                  Saving...
                </span>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog 
        open={isDeleteDialogOpen} 
        onOpenChange={(open) => {
          if (!isSubmitting) {
            setIsDeleteDialogOpen(open);
            if (!open) {
              setMemberToDelete(null);
            }
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this member? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setMemberToDelete(null);
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                  Deleting...
                </span>
              ) : (
                'Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lift member dialog */}
      <Dialog 
        open={isLiftDialogOpen} 
        onOpenChange={(open) => {
          if (!isSubmitting) {
            setIsLiftDialogOpen(open);
            if (!open) {
              setMemberToLift(null);
              setDisbursedAmount("");
            }
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Lift Member</DialogTitle>
            <DialogDescription>
              Enter the amount to be disbursed to this member.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="disbursed-amount">Disbursed Amount (₹)</Label>
              <Input
                id="disbursed-amount"
                type="number"
                placeholder="Enter disbursed amount"
                value={disbursedAmount}
                onChange={(e) => setDisbursedAmount(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsLiftDialogOpen(false);
                setMemberToLift(null);
                setDisbursedAmount("");
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleLiftConfirm} 
              disabled={isSubmitting || !disbursedAmount || parseFloat(disbursedAmount) <= 0}
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                  Confirming...
                </span>
              ) : (
                'Confirm'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Disbursed Amount Dialog */}
      <Dialog
        open={isUpdateDisbursedAmountDialogOpen}
        onOpenChange={(open) => {
          if (!isSubmitting) {
            setIsUpdateDisbursedAmountDialogOpen(open);
            if (!open) {
              setMemberToUpdateDisbursedAmount(null);
              setUpdatedDisbursedAmount("");
            }
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Disbursed Amount</DialogTitle>
            <DialogDescription>
              Update the disbursed amount for {memberToUpdateDisbursedAmount?.name}. The original lift date will remain unchanged.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="update-disbursed-amount">New Disbursed Amount (₹)</Label>
              <Input
                id="update-disbursed-amount"
                type="number"
                placeholder="Enter new disbursed amount"
                value={updatedDisbursedAmount}
                onChange={(e) => setUpdatedDisbursedAmount(e.target.value)}
              />
            </div>
            {memberToUpdateDisbursedAmount?.liftedDate && (
              <p className="text-sm text-muted-foreground">
                Original Lift Date: {format(new Date(memberToUpdateDisbursedAmount.liftedDate), "dd MMM yyyy")}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsUpdateDisbursedAmountDialogOpen(false);
                setMemberToUpdateDisbursedAmount(null);
                setUpdatedDisbursedAmount("");
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateDisbursedAmountConfirm} 
              disabled={isSubmitting || !updatedDisbursedAmount || parseFloat(updatedDisbursedAmount) <= 0}
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                  Updating...
                </span>
              ) : (
                'Update Amount'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload dialog */}
      <Dialog 
        open={isUploadDialogOpen} 
        onOpenChange={(open) => {
          if (!isSubmitting) {
            setIsUploadDialogOpen(open);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Members</DialogTitle>
            <DialogDescription>
              Upload an Excel file with member details. The file should have columns for Name, WhatsApp Number, Has Lifted, and Disbursed Amount.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="excel-file">Excel File</Label>
              <Input
                id="excel-file"
                type="file"
                accept=".xlsx,.xls"
                ref={fileInputRef}
                onChange={handleFileChange}
                disabled={isSubmitting}
              />
              <p className="text-sm text-muted-foreground mt-2">
                Download the template file first to see the required format.
              </p>
            </div>
            <Button type="button" variant="outline" onClick={handleDownloadTemplate}>
              Download Template
            </Button>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsUploadDialogOpen(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MemberList;
